// @flow
import { LIVE_AUCTION_ENDED, LIVE_AUCTION_OPENED, LIVE_AUCTION_STARTED } from './actions';
import { loadBatchEnd, loadBatchStart } from './fetchBatch';
import api from '../api/catalog';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';
import { LOAD_CATALOG_AD_SUCCESS } from './catalogAd';
import { LOAD_HOME_DATA_SUCCESS } from './home';
import { LOAD_ITEMS_SUCCESS } from './item';
import { LOAD_LIVE_CATALOG_STATUS_SUCCESS } from './liveCatalogStatus';
import { LOAD_SELLER_ENDED_CATALOGS_SUCCESS } from './sellerEndedCatalogs';
import { LOAD_SELLER_UPCOMING_CATALOGS_SUCCESS } from './sellerUpcomingCatalogs';
import { LOAD_STOREFRONT_ITEMS_SUCCESS } from './storefrontPage';
import { LOAD_UPCOMING_CATALOGS_SUCCESS } from './upcomingCatalogs';
import ms from 'ms';
import paymentProviders from '../../enums/paymentProviders';
import union from 'lodash/union';

/* Action Types */
export const LOAD_CATALOGS_FAIL = 'la/domain/catalogs/LOAD_FAIL';
export const LOAD_CATALOGS_REQUEST = 'la/domain/catalogs/LOAD_REQUEST';
export const LOAD_CATALOGS_SUCCESS = 'la/domain/catalogs/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');
const FETCH_SET_SIZE = 120;

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case LOAD_CATALOGS_FAIL:
            return {
                ...state,
                loading: difference(state.loading, action.meta.catalogIds),
            };
        case LOAD_CATALOGS_REQUEST:
            return {
                ...state,
                loading: union(state.loading, action.payload),
            };
        case LOAD_CATALOG_AD_SUCCESS:
        case LOAD_CATALOGS_SUCCESS:
        case LOAD_HOME_DATA_SUCCESS:
        case LOAD_ITEMS_SUCCESS:
        case LOAD_SELLER_UPCOMING_CATALOGS_SUCCESS:
        case LOAD_SELLER_ENDED_CATALOGS_SUCCESS:
        case LOAD_STOREFRONT_ITEMS_SUCCESS:
        case LOAD_UPCOMING_CATALOGS_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded };
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            if (action.payload.catalogs) {
                action.payload.catalogs.forEach(item => {
                    existing[item.catalogId] = {
                        paymentProvider: item.payrixPayments ? paymentProviders.PAYRIX : paymentProviders.STRIPE,
                        ...item,
                    };
                    delete existing[item.catalogId].payrixPayments;
                    loaded[item.catalogId] = time;
                    loading = difference(loading, [item.catalogId]);
                });
            }
            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        case LIVE_AUCTION_ENDED:
            existing = cloneDeep(state.byId);
            existing[action.payload.catalogId].catalogStatus = 'done';
            return {
                ...state,
                byId: existing,
            };
        case LOAD_LIVE_CATALOG_STATUS_SUCCESS:
            // Found a case from dante where the failure was not being transformed correctly.
            if (action.payload?.catalogId) {
                const { catalogId, status } = action.payload;
                existing = cloneDeep(state.byId);
                existing[catalogId] = existing[catalogId] || {};
                if (status === 'done' && existing[catalogId].catalogStatus !== 'done') {
                    existing[catalogId].catalogStatus = 'done';
                } else if (status === 'live' && existing[catalogId].catalogStatus !== 'done') {
                    existing[catalogId].catalogStatus = 'live';
                }

                return {
                    ...state,
                    byId: existing,
                };
            }
            return state;
        case LIVE_AUCTION_OPENED:
        case LIVE_AUCTION_STARTED:
            if (action.payload?.catalogId) {
                existing = cloneDeep(state.byId);
                existing[action.payload.catalogId].catalogStatus = 'live';
                return {
                    ...state,
                    byId: existing,
                };
            }
            return state;
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.catalog;
const globalStateSelector = (state: GlobalState) => state;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, state => state.byId);

const loadedSelector = createSelector(stateSelector, state => state.loaded);

const loadingSelector = createSelector(stateSelector, state => state.loading);

export const getCatalog = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || {});

const getLoadTimeForCatalog = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

const isCatalogLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

const shouldFetchCatalog = createSelector(
    [getCatalog, getLoadTimeForCatalog, isCatalogLoading],
    (catalog, loaded, loading) => {
        if (catalog) {
            const time = Date.now();
            const diff = time - loaded;
            if (diff < REDUX_STORE_TIME) return false;
        }
        return !loading;
    },
);

export const getCatalogIdsForSeller = createSelector([byIdSelector, idSelector], (byId, id) =>
    Object.keys(byId)
        .map(key => byId[key])
        .filter(catalog => catalog.sellerId === id && catalog.catalogStatus)
        .map(catalog => catalog.catalogId),
);

const whichCatalogsNeeded = createSelector([globalStateSelector, idSelector], (state, catalogIds) =>
    catalogIds.filter(catalogId => shouldFetchCatalog(state, catalogId)),
);

/* ACTION CREATORS */
const loadCatalogs = (catalogIds, loadingId, isFinalFetch) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            payload: catalogIds,
            type: LOAD_CATALOGS_REQUEST,
        });
        const response = await api.fetchCatalogsByIds({ authToken, catalogIds, deployment });
        dispatch({
            meta: { actionTime: Date.now(), catalogIds },
            payload: response.data,
            type: LOAD_CATALOGS_SUCCESS,
        });
        if (isFinalFetch) {
            dispatch(loadBatchEnd(loadingId));
        }
    } catch (error) {
        dispatch({
            error: true,
            meta: { catalogIds },
            payload: error,
            type: LOAD_CATALOGS_FAIL,
        });
        dispatch(loadBatchEnd(loadingId));
    }
};

const loadCatalogsInSets = (ids, setSize, loadingId) => {
    return dispatch => {
        if (ids.length > setSize) {
            return dispatch(loadCatalogs(ids.slice(0, setSize), loadingId, false)).then(() => {
                dispatch(loadCatalogsInSets(ids.slice(setSize), setSize, loadingId));
            });
        }
        return dispatch(loadCatalogs(ids, loadingId, true));
    };
};

export const fetchCatalogsIfNeeded = (catalogIds: number[], loadingId: string = 'none') => async (
    dispatch: Function,
    getState: Function,
) => {
    const needed = whichCatalogsNeeded(getState(), catalogIds);
    if (needed.length) {
        dispatch(loadBatchStart(loadingId));
        return dispatch(loadCatalogsInSets(needed, FETCH_SET_SIZE, loadingId));
    }
    return Promise.resolve();
};
